import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail, Phone, MapPin } from 'lucide-react';

export const Footer = () => {
  const socialLinks = {
    nutrition: { instagram: 'https://instagram.com/arteryvailnutrition', handle: '@arteryvailnutrition' },
    media: { instagram: 'https://instagram.com/arteryvailmedia', handle: '@arteryvailmedia' },
    fashion: { instagram: 'https://instagram.com/arteryvailfashion', handle: '@arteryvailfashion' }
  };

  return (
    <footer className="bg-[#0A0A0A] border-t border-[#3D2314]">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block" data-testid="footer-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_build-site-461/artifacts/if5761vx_Arteryvail-New-Logo-removebg-preview.png" 
                alt="ARTERYVAIL" 
                className="h-12 w-auto"
              />
            </Link>
            <p className="mt-4 text-sm text-[#A3988F] leading-relaxed max-w-sm">
              Three pillars of excellence. Elevating lives through nutrition, media artistry, and bespoke fashion.
            </p>
            <p className="mt-4 text-[#5C3D2E] font-serif italic text-lg">
              "Welcome. You're home now."
            </p>
          </div>

          {/* Nutrition Links */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-[#E5D3B3] mb-6">Nutrition</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/nutrition/calculator" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-calculator">
                  Health Calculator
                </Link>
              </li>
              <li>
                <Link to="/nutrition/shop" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-nutrition-shop">
                  Shop Products
                </Link>
              </li>
              <li>
                <Link to="/nutrition/meal-plans" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-meal-plans">
                  Meal Plans
                </Link>
              </li>
              <li>
                <Link to="/nutrition/consultations" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-consultations">
                  Consultations
                </Link>
              </li>
              <li>
                <a href={socialLinks.nutrition.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-sm text-[#5C3D2E] hover:text-[#E5D3B3] transition-colors">
                  <Instagram className="w-4 h-4" />
                  <span>{socialLinks.nutrition.handle}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Media Links */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-[#FAD6C6] mb-6">Media</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/media" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-portfolio">
                  Portfolio
                </Link>
              </li>
              <li>
                <Link to="/media#services" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-services">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/media#booking" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-media-booking">
                  Book Consultation
                </Link>
              </li>
              <li>
                <a href={socialLinks.media.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-sm text-[#5C3D2E] hover:text-[#FAD6C6] transition-colors">
                  <Instagram className="w-4 h-4" />
                  <span>{socialLinks.media.handle}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Fashion Links */}
          <div>
            <h4 className="text-sm font-semibold tracking-widest uppercase text-[#8C7B6B] mb-6">Fashion</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/fashion" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-shop">
                  Shop Collection
                </Link>
              </li>
              <li>
                <Link to="/fashion?category=outfits" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-outfits">
                  Outfits
                </Link>
              </li>
              <li>
                <Link to="/fashion?category=jewelry" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors" data-testid="footer-jewelry">
                  Jewelry
                </Link>
              </li>
              <li>
                <a href={socialLinks.fashion.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-sm text-[#5C3D2E] hover:text-[#8C7B6B] transition-colors">
                  <Instagram className="w-4 h-4" />
                  <span>{socialLinks.fashion.handle}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-16 pt-8 border-t border-[#3D2314]">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-[#3D2314] rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-[#E5D3B3]" />
              </div>
              <div>
                <p className="text-xs tracking-widest uppercase text-[#A3988F] mb-1">Email Us</p>
                <a href="mailto:info@arteryvailofficial.com" className="text-[#F5F2EB] hover:text-[#E5D3B3] transition-colors">
                  info@arteryvailofficial.com
                </a>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-[#3D2314] rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-[#E5D3B3]" />
              </div>
              <div>
                <p className="text-xs tracking-widest uppercase text-[#A3988F] mb-1">Call Us</p>
                <a href="tel:+14318444567" className="text-[#F5F2EB] hover:text-[#E5D3B3] transition-colors">
                  +1 (431) 844-4567
                </a>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 bg-[#3D2314] rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#E5D3B3]" />
              </div>
              <div>
                <p className="text-xs tracking-widest uppercase text-[#A3988F] mb-1">Visit Us</p>
                <p className="text-[#F5F2EB]">
                  Address coming soon
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#3D2314]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-[#A3988F]">
              © {new Date().getFullYear()} ARTERYVAIL. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors">Privacy Policy</a>
              <a href="#" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors">Terms of Service</a>
              <Link to="/contact" className="text-sm text-[#A3988F] hover:text-[#F5F2EB] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
